import { useSuspenseQuery } from '@apollo/client';
import React from 'react';
import { useParams } from 'react-router-dom';

import { H4, HStack, P, PageTitle, VStack } from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';

import {
  FactVerificationInputResultsListItem,
  inputResultsFragment,
} from './FactVerificationInputResultsListItem';

const query = graphql(
  `
    query getFactVerificationModelPromptTemplate($id: uuid!) {
      evalFactVerificationWorkflowModelPromptTemplatesByPk(id: $id) {
        __typename
        id
        inputs {
          ...inputResults
        }
        model_prompt_template {
          __typename
          id
          model_args {
            __typename
            id
            args
            modelType
          }
          template {
            __typename
            id
            name
          }
        }
        output_template {
          __typename
          id
          name
        }
      }
    }
  `,
  [inputResultsFragment],
);

export interface FactVerificationModelPromptTemplatePageProps {}

export const FactVerificationModelPromptTemplatePage: React.FC<
  FactVerificationModelPromptTemplatePageProps
> = () => {
  const { modelPromptTemplateId } = useParams() as {
    workflowId: string;
    modelPromptTemplateId: string;
  };

  const {
    data: {
      evalFactVerificationWorkflowModelPromptTemplatesByPk:
        workflowModelPromptTemplate,
    },
  } = useSuspenseQuery(query, {
    variables: {
      id: modelPromptTemplateId,
    },
  });

  const modelArgs =
    workflowModelPromptTemplate?.model_prompt_template?.model_args;
  const promptTemplate =
    workflowModelPromptTemplate?.model_prompt_template?.template;
  const outputTemplate = workflowModelPromptTemplate?.output_template;

  const inputs = workflowModelPromptTemplate?.inputs ?? [];

  return (
    <>
      <PageTitle>Workflow Results</PageTitle>
      <HStack gap={8} className="pb-8">
        <VStack>
          <H4>Model Info</H4>
          <P>{modelArgs?.modelType ?? ''}</P>
          <code className="rounded bg-bg-quaternary p-0.5 text-xs text-text-primary">
            {JSON.stringify(modelArgs?.args)}
          </code>
        </VStack>

        <VStack>
          <H4>Prompt Template</H4>
          <P>{promptTemplate?.name ?? ''}</P>
        </VStack>

        {outputTemplate && (
          <VStack>
            <H4>Output Template</H4>
            <P>{outputTemplate?.name ?? ''}</P>
          </VStack>
        )}
      </HStack>

      <H4> Results </H4>
      <ul className="mt-4 divide-y divide-border-primary">
        {inputs.map((i) => (
          <FactVerificationInputResultsListItem key={i.id} id={i.id} />
        ))}
      </ul>
    </>
  );
};
